import { Injectable, inject } from '@angular/core';
import { MembersService, Member } from './members.service';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class MembersTableService {
  private membersService = inject(MembersService);
  private loadingCache = new Map<string, boolean>();
  private dataCache = new Map<string, { data: Member[], timestamp: number }>();
  private readonly CACHE_DURATION = 30000; // 30 seconds

    // Helper to get loading state for a card
    public isLoading(cardId: string): boolean {
      return !!this.loadingCache.get(cardId);
    }

    // Helper to get paginated data for a card
    public getPaginatedData(cardId: string, pageIndex: number, pageSize: number): { data: Member[], total: number, hasMore: boolean, loading: boolean } {
      const cachedData = this.dataCache.get(cardId);
      const loading = this.isLoading(cardId);
      if (!cachedData) {
        return { data: [], total: 0, hasMore: false, loading };
      }
      const start = pageIndex * pageSize;
      const end = start + pageSize;
      const pagedData = cachedData.data.slice(start, end);
      const hasMore = end < cachedData.data.length;
      return {
        data: pagedData,
        total: cachedData.data.length,
        hasMore,
        loading
      };
    }
  private clearCache(cardId: string) {
    this.dataCache.delete(cardId);
    this.loadingCache.delete(cardId);
    console.log('[MembersTableService] Cleared cache for card:', cardId);
  }

  private validateCacheConsistency(cardId: string): boolean {
    const cachedData = this.dataCache.get(cardId);
    const serviceMembers = this.membersService.getMembers();
    
    if (cachedData && serviceMembers.length !== cachedData.data.length) {
      console.warn('[MembersTableService] Cache inconsistency detected - Cache:', cachedData.data.length, 'Service:', serviceMembers.length);
      return false;
    }
    return true;
  }

  async onLoadPage(event: any, tableRef: any): Promise<any> {
    try {
      const cardId = event.cardId;
      if (!cardId) {
        console.error('[MembersTableService] CardId is required for loading members');
        return { data: [], total: 0, hasMore: false, loading: false };
      }

      // Check if we're already loading this card's data
      if (this.loadingCache.get(cardId)) {
        console.log('[MembersTableService] Already loading data for card:', cardId, '- PREVENTING DUPLICATE CALL');
        const cachedData = this.dataCache.get(cardId);
        return {
          data: cachedData?.data || [],
          total: cachedData?.data.length || 0,
          hasMore: false,
          loading: true
        };
      }

      // Check if we have recent cached data
      const cachedData = this.dataCache.get(cardId);
      if (cachedData && (Date.now() - cachedData.timestamp) < this.CACHE_DURATION) {
        // Validate cache consistency with service data
        if (!this.validateCacheConsistency(cardId)) {
          console.log('[MembersTableService] Cache inconsistent, clearing and reloading');
          this.clearCache(cardId);
        } else {
          console.log('[MembersTableService] Using cached data for card:', cardId, '- CACHE HIT');
          console.log('[MembersTableService] Cached data contains:', cachedData.data.length, 'members');
          console.log('[MembersTableService] Sample cached member:', cachedData.data[0]);
          
          const result = {
            data: cachedData.data,
            total: cachedData.data.length,
            hasMore: false,
            loading: false
          };
          
          console.log('[MembersTableService] CACHE HIT - Returning to table:', result);
          return result;
        }
      }

      console.log('[MembersTableService] Loading members page for card:', cardId);
      
      // Debug current state
      this.debugCacheState(cardId);
      
      // Mark as loading to prevent duplicate calls
      this.loadingCache.set(cardId, true);
      
      try {
        // Check if service already has data for this card
        const currentMembers = this.membersService.getMembers();
        if (currentMembers.length > 0) {
          console.log('[MembersTableService] Using existing service data:', currentMembers.length, 'members');
          // Cache the existing data
          this.dataCache.set(cardId, {
            data: currentMembers,
            timestamp: Date.now()
          });
          return {
            data: currentMembers,
            total: currentMembers.length,
            hasMore: false,
            loading: false
          };
        }

        // If no existing data, fetch from API
        console.log('[MembersTableService] Fetching fresh data from API for card:', cardId);
        this.membersService.fetchMembers(cardId);
        
        // Wait for the data to load
        const members = await firstValueFrom(this.membersService.members$);
        
        // Only cache if we got actual data
        if (members && members.length >= 0) {
          this.dataCache.set(cardId, {
            data: members,
            timestamp: Date.now()
          });
          console.log('[MembersTableService] Fetched and cached', members.length, 'members from API');
          console.log('[MembersTableService] Cached members data:', members);
        }
        
        // Return the data in the format the table expects
        const result = {
          data: members,
          total: members.length,
          hasMore: false,
          loading: false
        };
        console.log('[MembersTableService] Returning to table:', result);
        return result;
      } finally {
        // Always clear the loading flag
        this.loadingCache.delete(cardId);
      }
    } catch (error) {
      console.error('[MembersTableService] Error loading members page:', error);
      // Clear loading flag on error
      if (event.cardId) {
        this.loadingCache.delete(event.cardId);
      }
      return { data: [], total: 0, hasMore: false, loading: false };
    }
  }

  async onGetTotal(event: any, tableRef: any): Promise<number> {
    try {
      const cardId = event.cardId;
      
      // First check if we have cached data
      const cachedData = this.dataCache.get(cardId);
      if (cachedData && (Date.now() - cachedData.timestamp) < this.CACHE_DURATION) {
        console.log('[MembersTableService] Total from cache:', cachedData.data.length);
        return cachedData.data.length;
      }
      
      // Fallback to service data
      const members = await firstValueFrom(this.membersService.members$);
      const total = members.length;
      console.log('[MembersTableService] Total members from service:', total);
      return total;
    } catch (error) {
      console.error('[MembersTableService] Error getting members total:', error);
      return 0;
    }
  }

  async onAdd(event: any): Promise<Member> {
  const cardId = event.cardId; // or get from elsewhere if needed
  if (!cardId) throw new Error('CardId is required for adding member');

  // Extract the actual form data
  const formData = event.item;
  console.log('[MembersTableService] Extracted formData:', formData);

  // Defensive: trim all values
  const member: Member = {
    name: typeof formData.name === 'string' ? formData.name.trim() : '',
    email: typeof formData.email === 'string' ? formData.email.trim() : '',
    phone: typeof formData.phone === 'string' ? formData.phone.trim() : '',
    whatsappNumber: typeof formData.whatsappNumber === 'string' ? formData.whatsappNumber.trim() : '',
    department: typeof formData.department === 'string' ? formData.department.trim() : '',
    jobTitle: typeof formData.jobTitle === 'string' ? formData.jobTitle.trim() : '',
    handler: typeof formData.handler === 'string' ? formData.handler.trim() : '',
    leaderRank: typeof formData.leaderRank === 'string' ? formData.leaderRank.trim() : '',
    leaderTitle: typeof formData.leaderTitle === 'string' ? formData.leaderTitle.trim() : '',

    /// For auto-generating handler
    // handler: typeof formData.handler === 'string'
    //   ? formData.handler.trim()
    //   : (typeof formData.name === 'string' ? formData.name.trim().toLowerCase().replace(/\s+/g, '_') : 'member'),
    status: 'active',
    cardId,
    // other fields as needed
  };

  console.log('[MembersTableService] Member object sent to MembersService:', member);

  const newMember = await firstValueFrom(this.membersService.createMember(cardId, member));
  this.clearCache(cardId);
  return newMember;
}

  async onUpdate(event: any): Promise<Member> {
    try {
      const cardId = event.cardId;
      const memberId = event.id;
      
      if (!cardId || !memberId) {
        throw new Error('CardId and memberId are required for updating member');
      }

      console.log('[MembersTableService] Updating member:', memberId, event.data);
      
      const updatedMember = await firstValueFrom(
        this.membersService.updateMember(cardId, memberId, event.data)
      );
      
      // Clear cache to force refresh
      this.clearCache(cardId);
      
      console.log('[MembersTableService] Member updated successfully:', updatedMember.id);
      return updatedMember;
    } catch (error) {
      console.error('[MembersTableService] Error updating member:', error);
      throw error;
    }
  }

  async onDelete(event: any): Promise<{ success: boolean; message: string }> {
    try {
      const cardId = event.cardId;
      const memberId = event.id;
      
      if (!cardId || !memberId) {
        throw new Error('CardId and memberId are required for deleting member');
      }

      console.log('[MembersTableService] Deleting member:', memberId);
      
      const result = await firstValueFrom(
        this.membersService.deleteMember(cardId, memberId)
      );
      
      // Clear cache to force refresh
      this.clearCache(cardId);
      
      console.log('[MembersTableService] Member deleted successfully:', memberId);
      return result;
    } catch (error) {
      console.error('[MembersTableService] Error deleting member:', error);
      throw error;
    }
  }

  async onDeleteBatch(event: any): Promise<{ success: boolean; message: string }[]> {
    try {
      const cardId = event.cardId;
      const memberIds = event.ids;
      
      if (!cardId || !memberIds || !Array.isArray(memberIds)) {
        throw new Error('CardId and member IDs array are required for batch delete');
      }

      console.log('[MembersTableService] Deleting members batch:', memberIds);
      
      // Delete members in parallel
      const deletePromises = memberIds.map(id => 
        firstValueFrom(this.membersService.deleteMember(cardId, id))
      );
      
      const results = await Promise.all(deletePromises);
      
      // Clear cache to force refresh
      this.clearCache(cardId);
      
      console.log('[MembersTableService] Batch delete completed:', results.length, 'members deleted');
      return results;
    } catch (error) {
      console.error('[MembersTableService] Error deleting members batch:', error);
      throw error;
    }
  }

  async onDeleteAll(cardId?: string): Promise<void> {
    try {
      if (!cardId) {
        console.warn('[MembersTableService] Delete all members - no cardId provided');
        return;
      }

      const members = this.membersService.getMembers();
      if (members.length === 0) {
        console.log('[MembersTableService] No members to delete');
        return;
      }

      // Confirm deletion with user (this should be handled by the UI)
      console.warn('[MembersTableService] Deleting all', members.length, 'members');
      
      const memberIds = members.map(m => m.id).filter(id => id) as string[];
      await this.onDeleteBatch({ cardId, ids: memberIds });
      
      console.log('[MembersTableService] All members deleted successfully');
    } catch (error) {
      console.error('[MembersTableService] Error deleting all members:', error);
      throw error;
    }
  }

  async onUploadFile(event: any): Promise<void> {
    try {
      console.log('[MembersTableService] File upload:', event);
      // Handle file upload if needed - this might be used for CSV import
      // Implementation would depend on the specific file upload requirements
    } catch (error) {
      console.error('[MembersTableService] Error uploading file:', error);
      throw error;
    }
  }

  async onImportData(event: any): Promise<{ imported: Member[], errors: any[] }> {
    try {
      const cardId = event.cardId;
      if (!cardId) throw new Error('CardId is required for importing members');
      if (!event.items || !Array.isArray(event.items)) throw new Error('Import data must contain an array of items');

      const result = await this.membersService.importMembers(event.items, cardId);
      this.clearCache(cardId);

      return result; // <-- Return the result with imported and errors
    } catch (error) {
      console.error('[MembersTableService] Error importing members data:', error);
      throw error;
    }
  }

  // Export members to CSV
  async exportMembersToCSV(): Promise<void> {
    try {
      const members = this.membersService.getMembers();
      if (members.length === 0) {
        console.warn('[MembersTableService] No members to export');
        return;
      }

      console.log('[MembersTableService] Exporting', members.length, 'members to CSV');

      const headers = ['name', 'email', 'phone', 'whatsApp', 'department', 'jobTitle', 'handler','leaderRank','leaderTitle', 'status', 'Join Date'];
      const csvContent = [
        headers.join(','),
        ...members.map(member => [
          `"${member.name || ''}"`,
          `"${member.email || ''}"`,
          `"${member.phone || ''}"`,
          `"${member.whatsappNumber || ''}"`,
          `"${member.department || ''}"`,
          `"${member.jobTitle || ''}"`,
          `"${member.handler || ''}"`,
          `"${member.leaderRank || ''}"`,
          `"${member.leaderTitle || ''}"`,
          `"${member.status || 'active'}"`,
          `"${member.joinDate ? new Date(member.joinDate).toISOString().split('T')[0] : ''}"`
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `members_${new Date().toISOString().split('T')[0]}.csv`);
      link.classList.add('invisible');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('[MembersTableService] CSV export completed');
    } catch (error) {
      console.error('[MembersTableService] Error exporting members to CSV:', error);
      throw error;
    }
  }

  // Public method to clear cache manually
  public clearMembersCache(cardId: string): void {
    this.clearCache(cardId);
  }

  // Method to clear all caches
  public clearAllCaches(): void {
    this.loadingCache.clear();
    this.dataCache.clear();
    console.log('[MembersTableService] Cleared all caches');
  }

  // Method to force refresh cache with current service data
  public refreshCache(cardId: string): void {
    const currentMembers = this.membersService.getMembers();
    this.dataCache.set(cardId, {
      data: currentMembers,
      timestamp: Date.now()
    });
    console.log('[MembersTableService] Refreshed cache with', currentMembers.length, 'members');
  }

  // Debug method to check cache and service state
  public debugCacheState(cardId: string): void {
    const cachedData = this.dataCache.get(cardId);
    const serviceMembers = this.membersService.getMembers();
    console.log('[MembersTableService] DEBUG - Cache state:');
    console.log('  - Cache exists:', !!cachedData);
    console.log('  - Cache members:', cachedData?.data.length || 0);
    console.log('  - Service members:', serviceMembers.length);
    console.log('  - Cache timestamp:', cachedData?.timestamp);
    console.log('  - Cache age:', cachedData ? Date.now() - cachedData.timestamp : 'N/A');
  }
}